// @ts-check

/**
 * Hero carousel Web Component. Manages slide transitions, autoplay,
 * progress indicators, swipe navigation, and keyboard controls.
 *
 * Single slide: static hero, no carousel behavior.
 * Multi-slide: crossfade transitions with progress line animation.
 *
 * @attr autoplay - Cycle speed in seconds (e.g., "8"). Omit to disable.
 * @attr data-paused - Set by JS when autoplay is paused (hover, focus, hidden tab).
 */
(() => {
  class HeroCarousel extends HTMLElement {
    connectedCallback() {
      /** @type {HTMLElement[]} */
      this._slides = [...this.querySelectorAll('.hero__slide')];
      /** @type {HTMLButtonElement[]} */
      this._progressLines = [...this.querySelectorAll('.hero__progress-line')];
      /** @type {number} */
      this._currentIndex = 0;
      /** @type {number | null} */
      this._timer = null;
      /** @type {boolean} */
      this._isPaused = false;
      /** @type {number} */
      this._pointerStartX = 0;
      /** @type {number} */
      this._pointerStartY = 0;

      if (this._slides.length <= 1) return;

      const cycleSpeed = this.getAttribute('autoplay');
      /** @type {number} */
      this._cycleMs = cycleSpeed ? parseFloat(cycleSpeed) * 1000 : 0;

      // Disable autoplay in theme editor
      if (window.Shopify?.designMode) {
        this._cycleMs = 0;
      }

      // Reduced motion: disable autoplay, pause videos
      /** @type {MediaQueryList} */
      this._motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (this._motionQuery.matches) {
        this._cycleMs = 0;
        this._pauseAllVideos();
      }
      this._onMotionChange = this._handleMotionChange.bind(this);
      this._motionQuery.addEventListener('change', this._onMotionChange);

      // Mobile media swap
      /** @type {MediaQueryList} */
      this._mobileQuery = window.matchMedia('(max-width: 749px)');
      this._onMobileChange = this._handleMobileChange.bind(this);
      this._mobileQuery.addEventListener('change', this._onMobileChange);
      this._handleMobileChange(this._mobileQuery);

      // Autoplay
      if (this._cycleMs > 0) {
        this._startAutoplay();
      }

      // Hover pause
      this._onMouseEnter = () => this._pause();
      this._onMouseLeave = () => this._resume();
      this.addEventListener('mouseenter', this._onMouseEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);

      // Focus pause
      this._onFocusIn = () => this._pause();
      this._onFocusOut = (/** @type {FocusEvent} */ e) => {
        if (!this.contains(/** @type {Node} */ (e.relatedTarget))) {
          this._resume();
        }
      };
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);

      // Tab visibility
      this._onVisibility = () => {
        if (document.hidden) {
          this._pause();
        } else {
          this._resume();
        }
      };
      document.addEventListener('visibilitychange', this._onVisibility);

      // Progress line clicks
      this._progressLines.forEach((btn, i) => {
        btn.addEventListener('click', () => {
          this._goToSlide(i);
          this._restartAutoplay();
        });
      });

      // Keyboard navigation
      this._onKeydown = (/** @type {KeyboardEvent} */ e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this._goToSlide((this._currentIndex - 1 + this._slides.length) % this._slides.length);
          this._restartAutoplay();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this._goToSlide((this._currentIndex + 1) % this._slides.length);
          this._restartAutoplay();
        }
      };
      this.addEventListener('keydown', this._onKeydown);

      // Swipe detection via pointer events
      this._onPointerDown = (/** @type {PointerEvent} */ e) => {
        this._pointerStartX = e.clientX;
        this._pointerStartY = e.clientY;
      };
      this._onPointerUp = (/** @type {PointerEvent} */ e) => {
        const dx = e.clientX - this._pointerStartX;
        const dy = e.clientY - this._pointerStartY;
        // Only register horizontal swipes (dx > dy and threshold 30px)
        if (Math.abs(dx) > 30 && Math.abs(dx) > Math.abs(dy)) {
          if (dx < 0) {
            // Swipe left → next
            this._goToSlide((this._currentIndex + 1) % this._slides.length);
          } else {
            // Swipe right → prev
            this._goToSlide((this._currentIndex - 1 + this._slides.length) % this._slides.length);
          }
          this._restartAutoplay();
        }
      };
      this.addEventListener('pointerdown', this._onPointerDown);
      this.addEventListener('pointerup', this._onPointerUp);
    }

    disconnectedCallback() {
      this._stopAutoplay();
      if (this._onVisibility) {
        document.removeEventListener('visibilitychange', this._onVisibility);
      }
      if (this._motionQuery && this._onMotionChange) {
        this._motionQuery.removeEventListener('change', this._onMotionChange);
      }
      if (this._mobileQuery && this._onMobileChange) {
        this._mobileQuery.removeEventListener('change', this._onMobileChange);
      }
    }

    /** @param {number} index */
    _goToSlide(index) {
      if (index === this._currentIndex) return;

      const prev = this._slides[this._currentIndex];
      const next = this._slides[index];

      // Update slides
      prev.classList.remove('hero__slide--active');
      prev.setAttribute('aria-hidden', 'true');
      next.classList.add('hero__slide--active');
      next.removeAttribute('aria-hidden');

      // Update progress lines
      if (this._progressLines.length > 0) {
        this._progressLines[this._currentIndex].removeAttribute('aria-current');
        this._progressLines[index].setAttribute('aria-current', 'true');
      }

      this._currentIndex = index;
    }

    _startAutoplay() {
      if (this._cycleMs <= 0 || this._timer) return;
      this._timer = window.setInterval(() => {
        this._goToSlide((this._currentIndex + 1) % this._slides.length);
      }, this._cycleMs);
      this.removeAttribute('data-paused');
    }

    _stopAutoplay() {
      if (this._timer) {
        clearInterval(this._timer);
        this._timer = null;
      }
    }

    _pause() {
      if (this._cycleMs <= 0) return;
      this._isPaused = true;
      this._stopAutoplay();
      this.setAttribute('data-paused', '');
    }

    _resume() {
      if (this._cycleMs <= 0 || !this._isPaused) return;
      this._isPaused = false;
      this._startAutoplay();
    }

    _restartAutoplay() {
      if (this._cycleMs <= 0) return;
      this._stopAutoplay();
      this._isPaused = false;

      // Re-trigger progress animation by toggling aria-current
      if (this._progressLines.length > 0) {
        const active = this._progressLines[this._currentIndex];
        active.removeAttribute('aria-current');
        // Force reflow to restart CSS animation
        void active.offsetWidth;
        active.setAttribute('aria-current', 'true');
      }

      this._startAutoplay();
      this.removeAttribute('data-paused');
    }

    /** @param {MediaQueryList | MediaQueryListEvent} mql */
    _handleMobileChange(mql) {
      const isMobile = mql.matches;
      this._slides.forEach((slide) => {
        const video = slide.querySelector('video[data-mobile-src]');
        if (!video) return;
        const source = video.querySelector('source');
        if (!source) return;
        const src = isMobile
          ? video.getAttribute('data-mobile-src')
          : video.getAttribute('data-desktop-src');
        if (src && source.getAttribute('src') !== src) {
          source.setAttribute('src', src);
          /** @type {HTMLVideoElement} */ (video).load();
        }
      });
    }

    /** @param {MediaQueryListEvent} e */
    _handleMotionChange(e) {
      if (e.matches) {
        this._cycleMs = 0;
        this._stopAutoplay();
        this._pauseAllVideos();
      }
    }

    _pauseAllVideos() {
      this.querySelectorAll('video').forEach((v) => {
        /** @type {HTMLVideoElement} */ (v).pause();
      });
    }
  }

  customElements.define('hero-carousel', HeroCarousel);
})();
